import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Upload,
  Download,
  Copy,
  RefreshCw,
  User,
  Edit3,
  Trash2,
  FileSpreadsheet,
  FileText,
  CheckCircle2,
  AlertCircle,
  Check,
  Search,
  X,
  Users,
  UserCheck,
  UserX,
  ChevronDown,
  MessageSquare,
  Folder,
  Layers,
  Sparkles,
} from "lucide-react";
import * as XLSX from "xlsx";
import {
  listContacts,
  createContact,
  updateContact,
  updateLeadStatus,
  deleteContact,
  bulkImportContacts,
  listTags,
  listLeadLists,
  type CurrentUser,
  type Contact,
  type Tag,
  type LeadListSummary,
} from "../../lib/api";

interface ParsedContactPreview {
  phone_e164: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  isValid: boolean;
  error?: string;
}

export function ContactsView({
  token,
  user,
  tenantId,
}: {
  token: string;
  user: CurrentUser;
  tenantId?: string;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const effectiveTenantId =
    user.role === "tenant"
      ? user.tenant_id
      : tenantId && tenantId !== "all"
      ? tenantId
      : user.tenant_id;

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTagId, setSelectedTagId] = useState<string | "all">("all");
  const [selectedListName, setSelectedListName] = useState<string | "all">("all");
  const [pipelineStageFilter, setPipelineStageFilter] = useState<string | "all">("all");
  const [statusFilter, setStatusFilter] = useState<string | "all">("all");
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Modals
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [deletingContact, setDeletingContact] = useState<Contact | null>(null);
  const [showExportMenu, setShowExportMenu] = useState(false);

  // Edit state
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editOptIn, setEditOptIn] = useState("opted_in");

  // Add Contact state
  const [phoneE164, setPhoneE164] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");

  // Import state
  const [importTab, setImportTab] = useState<"file" | "paste">("file");
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importListName, setImportListName] = useState("");
  const [importLeadStatus, setImportLeadStatus] = useState("new");
  const [importTagId, setImportTagId] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [parsedRows, setParsedRows] = useState<ParsedContactPreview[]>([]);
  const [importError, setImportError] = useState<string | null>(null);
  const [pasteText, setPasteText] = useState("");
  const [importProgress, setImportProgress] = useState<{
    total: number;
    current: number;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Live Contacts Query
  const contactsQuery = useQuery({
    queryKey: ["contacts", searchQuery, selectedTagId, selectedListName, pipelineStageFilter, effectiveTenantId],
    queryFn: () =>
      listContacts(
        token,
        searchQuery || undefined,
        selectedTagId === "all" ? undefined : selectedTagId,
        effectiveTenantId,
        selectedListName === "all" ? undefined : selectedListName,
        pipelineStageFilter === "all" ? undefined : pipelineStageFilter
      ),
    refetchInterval: 15_000,
  });

  // Live Tags Query
  const tagsQuery = useQuery({
    queryKey: ["contacts", "tags", effectiveTenantId],
    queryFn: () => listTags(token, effectiveTenantId),
  });

  // Live Lead Lists Query
  const leadListsQuery = useQuery({
    queryKey: ["lead-lists", effectiveTenantId],
    queryFn: () => listLeadLists(token, effectiveTenantId),
  });

  const rawContacts = contactsQuery.data || [];
  const tags = tagsQuery.data || [];
  const leadLists = leadListsQuery.data || [];

  // Update Lead Status Mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ contactId, status }: { contactId: string; status: string }) =>
      updateLeadStatus(token, contactId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.invalidateQueries({ queryKey: ["lead-lists"] });
    },
    onError: (err: any) => {
      alert(`Failed to update lead status: ${err.message || "Unknown error"}`);
    },
  });

  // Filter contacts by status filter if selected
  const contacts = rawContacts.filter((c) => {
    if (statusFilter === "all") return true;
    return c.opt_in_status === statusFilter;
  });

  const totalCount = rawContacts.length;
  const optedInCount = rawContacts.filter((c) => c.opt_in_status === "opted_in").length;
  const optedOutCount = rawContacts.filter((c) => c.opt_in_status === "opted_out").length;

  function formatPhone(raw: string): string {
    let clean = raw.trim().replace(/[^0-9+]/g, "");
    if (!clean.startsWith("+")) {
      clean = "+" + clean;
    }
    return clean;
  }

  function validatePhoneNumber(raw: string): { isValid: boolean; formatted: string; error?: string } {
    let clean = raw.trim().replace(/[^0-9+]/g, "");
    if (!clean) return { isValid: false, formatted: "", error: "Phone number missing" };
    if (!clean.startsWith("+")) {
      clean = "+" + clean;
    }
    const digitsOnly = clean.replace(/[^0-9]/g, "");
    if (digitsOnly.length < 7 || digitsOnly.length > 15) {
      return { isValid: false, formatted: clean, error: "Must be 7-15 digits" };
    }
    return { isValid: true, formatted: clean };
  }

  // Create Contact Mutation
  const createContactMutation = useMutation({
    mutationFn: () =>
      createContact(
        token,
        {
          phone_e164: formatPhone(phoneE164),
          first_name: firstName.trim() || undefined,
          last_name: lastName.trim() || undefined,
          email: email.trim() || undefined,
        },
        effectiveTenantId
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      setIsAddModalOpen(false);
      setPhoneE164("");
      setFirstName("");
      setLastName("");
      setEmail("");
    },
    onError: (err: any) => {
      alert(`Failed to add contact: ${err.message || "Unknown error"}`);
    },
  });

  // Update Contact Mutation
  const updateContactMutation = useMutation({
    mutationFn: () => {
      if (!editingContact) throw new Error("No contact selected for edit");
      return updateContact(token, editingContact.id, {
        first_name: editFirstName.trim() || undefined,
        last_name: editLastName.trim() || undefined,
        email: editEmail.trim() || undefined,
        opt_in_status: editOptIn,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      setEditingContact(null);
    },
    onError: (err: any) => {
      alert(`Failed to update contact: ${err.message || "Unknown error"}`);
    },
  });

  // Delete Contact Mutation
  const deleteContactMutation = useMutation({
    mutationFn: (contactId: string) => deleteContact(token, contactId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      setDeletingContact(null);
    },
    onError: (err: any) => {
      alert(`Failed to delete contact: ${err.message || "Unknown error"}`);
    },
  });

  // Bulk Import Mutation
  const importMutation = useMutation({
    mutationFn: async (contactsToImport: ParsedContactPreview[]) => {
      const valid = contactsToImport.filter((c) => c.isValid);
      if (valid.length === 0) {
        throw new Error("No valid contacts to import");
      }

      const chunkSize = 500;
      let importedTotal = 0;
      let updatedTotal = 0;

      for (let i = 0; i < valid.length; i += chunkSize) {
        const batch = valid.slice(i, i + chunkSize).map((c) => ({
          phone_e164: c.phone_e164,
          first_name: c.first_name,
          last_name: c.last_name,
          email: c.email,
        }));

        setImportProgress({ total: valid.length, current: i + batch.length });
        const res = await bulkImportContacts(
          token,
          {
            contacts: batch,
            list_name: importListName.trim() || undefined,
            lead_status: importLeadStatus || "new",
            tag_id: importTagId || undefined,
          },
          effectiveTenantId
        );
        importedTotal += res.imported;
        updatedTotal += res.updated;
      }

      return {
        imported: importedTotal,
        updated: updatedTotal,
        total: valid.length,
        list_name: importListName.trim() || undefined,
      };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.invalidateQueries({ queryKey: ["lead-lists"] });
      setIsImportModalOpen(false);
      setParsedRows([]);
      setImportFile(null);
      setPasteText("");
      setImportListName("");
      setImportLeadStatus("new");
      setImportTagId("");
      setImportProgress(null);
      alert(
        `Import complete!\n• List: ${result.list_name || "General"}\n• Added: ${result.imported} new contacts\n• Updated: ${result.updated} existing contacts`
      );
    },
    onError: (err: any) => {
      setImportProgress(null);
      alert(`Failed to import contacts: ${err.message || "Unknown error"}`);
    },
  });

  // Parse Excel / CSV files
  async function handleFileUpload(file: File) {
    setImportError(null);
    setImportFile(file);

    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      const firstSheetName = workbook.SheetNames[0];
      if (!firstSheetName) {
        throw new Error("Workbook does not contain any sheets.");
      }

      const worksheet = workbook.Sheets[firstSheetName];
      if (!worksheet) {
        throw new Error("Worksheet could not be read.");
      }
      const rawJson = XLSX.utils.sheet_to_json<Record<string, any>>(worksheet, {
        defval: "",
        raw: false,
      });

      if (rawJson.length === 0) {
        throw new Error("No data rows found in this sheet.");
      }

      // Map columns automatically based on header names
      const headers = Object.keys(rawJson[0] || {});
      const phoneCol = headers.find((h) =>
        /phone|mobile|contact|number|whatsapp|tel|cell/i.test(h)
      );
      const firstNameCol = headers.find((h) =>
        /first.*name|fname|given.*name/i.test(h)
      );
      const nameCol = headers.find((h) =>
        /^(name|full.*name|contact.*name)$/i.test(h)
      );
      const lastNameCol = headers.find((h) =>
        /last.*name|lname|surname|family.*name/i.test(h)
      );
      const emailCol = headers.find((h) => /email|e-mail|mail/i.test(h));

      const parsed: ParsedContactPreview[] = rawJson.map((row) => {
        const rawPhone = phoneCol ? String(row[phoneCol] || "") : "";
        let fName = firstNameCol ? String(row[firstNameCol] || "").trim() : "";
        let lName = lastNameCol ? String(row[lastNameCol] || "").trim() : "";

        if (!fName && nameCol) {
          const full = String(row[nameCol] || "").trim();
          const parts = full.split(" ");
          fName = parts[0] || "";
          lName = parts.slice(1).join(" ");
        }

        const em = emailCol ? String(row[emailCol] || "").trim() : "";
        const val = validatePhoneNumber(rawPhone);

        return {
          phone_e164: val.formatted || rawPhone,
          first_name: fName || undefined,
          last_name: lName || undefined,
          email: em || undefined,
          isValid: val.isValid,
          error: val.error,
        };
      });

      setParsedRows(parsed);
    } catch (err: any) {
      setImportError(err.message || "Failed to parse file.");
      setParsedRows([]);
    }
  }

  // Parse pasted CSV / Tab-separated text
  function handlePasteParse(text: string) {
    setPasteText(text);
    setImportError(null);
    if (!text.trim()) {
      setParsedRows([]);
      return;
    }

    const lines = text.trim().split(/\r?\n/);
    const parsed: ParsedContactPreview[] = [];

    for (let i = 0; i < lines.length; i++) {
      const line = (lines[i] || "").trim();
      if (!line) continue;
      if (i === 0 && /phone|mobile|name|contact/i.test(line)) continue;

      const delimiter = line.includes("\t") ? "\t" : ",";
      const parts = line.split(delimiter).map((p) => p.trim().replace(/^"|"$/g, ""));

      const rawPhone = parts[0] || "";
      const fName = parts[1] || "";
      const lName = parts[2] || "";
      const em = parts[3] || "";

      const val = validatePhoneNumber(rawPhone);
      parsed.push({
        phone_e164: val.formatted || rawPhone,
        first_name: fName || undefined,
        last_name: lName || undefined,
        email: em || undefined,
        isValid: val.isValid,
        error: val.error,
      });
    }

    setParsedRows(parsed);
  }

  // Download Sample Excel Template
  function downloadSampleExcel() {
    const sampleData = [
      {
        "Phone Number": "+919876543210",
        "First Name": "Rajesh",
        "Last Name": "Kumar",
        "Email": "rajesh@example.com",
      },
      {
        "Phone Number": "+919820011223",
        "First Name": "Priya",
        "Last Name": "Sharma",
        "Email": "priya.sharma@example.com",
      },
      {
        "Phone Number": "+14155552671",
        "First Name": "Alex",
        "Last Name": "Morgan",
        "Email": "alex.m@example.com",
      },
    ];

    const ws = XLSX.utils.json_to_sheet(sampleData);
    ws["!cols"] = [{ wch: 18 }, { wch: 15 }, { wch: 15 }, { wch: 25 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Contacts Template");
    XLSX.writeFile(wb, "wamaster_contacts_sample.xlsx");
  }

  // Download Sample CSV
  function downloadSampleCsv() {
    const csvContent =
      "Phone Number,First Name,Last Name,Email\n+919876543210,Rajesh,Kumar,rajesh@example.com\n+919820011223,Priya,Sharma,priya.sharma@example.com\n+14155552671,Alex,Morgan,alex.m@example.com";
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "wamaster_contacts_sample.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // Export all contacts to Excel (.xlsx)
  function exportExcel() {
    if (contacts.length === 0) {
      alert("No contacts available to export.");
      return;
    }

    const exportRows = contacts.map((c) => ({
      "Phone Number": c.phone_e164,
      "First Name": c.first_name || "",
      "Last Name": c.last_name || "",
      "Email": c.email || "",
      "Opt-In Status": c.opt_in_status,
      "Tags": c.tags ? c.tags.map((t) => t.name).join(", ") : "",
      "Created At": new Date(c.created_at).toISOString().slice(0, 10),
    }));

    const ws = XLSX.utils.json_to_sheet(exportRows);
    ws["!cols"] = [
      { wch: 18 },
      { wch: 15 },
      { wch: 15 },
      { wch: 25 },
      { wch: 14 },
      { wch: 20 },
      { wch: 12 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Contacts");
    XLSX.writeFile(
      wb,
      `contacts_export_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  }

  // Export all contacts to CSV (.csv)
  function exportCsv() {
    if (contacts.length === 0) {
      alert("No contacts available to export.");
      return;
    }

    const headers = ["Phone Number,First Name,Last Name,Email,OptInStatus,Tags,CreatedAt"];
    const rows = contacts.map(
      (c) =>
        `"${c.phone_e164}","${c.first_name || ""}","${c.last_name || ""}","${c.email || ""}","${c.opt_in_status}","${
          c.tags ? c.tags.map((t) => t.name).join(";") : ""
        }","${new Date(c.created_at).toISOString().slice(0, 10)}"`
    );
    const csvContent = "data:text/csv;charset=utf-8," + [headers, ...rows].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute(
      "download",
      `contacts_export_${new Date().toISOString().slice(0, 10)}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  function copyText(text: string, id: string = text) {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1500);
  }

  const validParsedCount = parsedRows.filter((r) => r.isValid).length;
  const invalidParsedCount = parsedRows.length - validParsedCount;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <div className="inline-flex items-center gap-2 px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold mb-1">
            <span>Unlimited Contacts Storage</span>
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            <span className="font-mono font-bold">Zero List Size Penalties</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <Users className="w-6 h-6 text-emerald-600" />
            Contacts CRM
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Manage customer phone numbers, opt-in status, and import unlimited spreadsheets with custom attributes.
          </p>
        </div>


        <div className="flex items-center gap-2 relative">
          <button
            onClick={() => {
              setIsImportModalOpen(true);
              setParsedRows([]);
              setImportFile(null);
              setImportError(null);
            }}
            className="btn-secondary text-xs py-2 px-3.5 flex items-center gap-1.5 shadow-2xs hover:border-emerald-500 hover:text-emerald-700 transition-colors"
          >
            <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
            <span>Import Excel / CSV</span>
          </button>

          {/* Export Dropdown (Protected: Hidden for Support Agents) */}
          {user.role !== "agent" && (
            <div className="relative">
              <button
                onClick={() => setShowExportMenu(!showExportMenu)}
                className="btn-secondary text-xs py-2 px-3 flex items-center gap-1.5 shadow-2xs"
              >
                <Download className="w-3.5 h-3.5 text-slate-500" />
                <span>Export</span>
                <ChevronDown className="w-3 h-3 text-slate-400" />
              </button>

              {showExportMenu && (
                <div
                  className="absolute right-0 mt-1 w-44 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-20"
                  onMouseLeave={() => setShowExportMenu(false)}
                >
                  <button
                    onClick={() => {
                      exportExcel();
                      setShowExportMenu(false);
                    }}
                    className="w-full text-left px-3 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                  >
                    <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" />
                    <span>Excel (.xlsx)</span>
                  </button>
                  <button
                    onClick={() => {
                      exportCsv();
                      setShowExportMenu(false);
                    }}
                    className="w-full text-left px-3 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                  >
                    <FileText className="w-3.5 h-3.5 text-blue-600" />
                    <span>CSV (.csv)</span>
                  </button>
                </div>
              )}
            </div>
          )}

          <button
            onClick={() => {
              setPhoneE164("");
              setFirstName("");
              setLastName("");
              setEmail("");
              setIsAddModalOpen(true);
            }}
            className="btn-primary text-xs py-2 px-3.5 flex items-center gap-1.5 shadow-2xs"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Add Contact</span>
          </button>
        </div>
      </div>

      {/* KPI Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-slate-500">Total Contacts</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{totalCount.toLocaleString()}</p>
          </div>
          <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600">
            <Users className="w-5 h-5" />
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-slate-500">Opted In (Active)</p>
            <p className="text-2xl font-bold text-emerald-600 mt-1">{optedInCount.toLocaleString()}</p>
          </div>
          <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600">
            <UserCheck className="w-5 h-5" />
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-slate-500">Opted Out / Unsubscribed</p>
            <p className="text-2xl font-bold text-slate-700 mt-1">{optedOutCount.toLocaleString()}</p>
          </div>
          <div className="w-10 h-10 rounded-lg bg-rose-50 flex items-center justify-center text-rose-600">
            <UserX className="w-5 h-5" />
          </div>
        </div>
      </div>

      {/* Lead Lists Quick Filter Bar */}
      {leadLists.length > 0 && (
        <div className="flex items-center gap-2 overflow-x-auto pb-1 text-xs">
          <span className="text-slate-400 font-semibold flex items-center gap-1 shrink-0">
            <Layers className="w-3.5 h-3.5 text-emerald-600" />
            Lead Lists:
          </span>
          <button
            onClick={() => setSelectedListName("all")}
            className={`px-2.5 py-1 rounded-lg font-medium transition-all shrink-0 ${
              selectedListName === "all"
                ? "bg-emerald-600 text-white shadow-2xs"
                : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
            }`}
          >
            All Contacts
          </button>
          {leadLists.map((l: LeadListSummary) => (
            <button
              key={l.list_name}
              onClick={() => setSelectedListName(l.list_name)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg font-medium transition-all shrink-0 ${
                selectedListName === l.list_name
                  ? "bg-emerald-600 text-white shadow-2xs"
                  : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
            >
              <span>📁 {l.list_name}</span>
              <span
                className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
                  selectedListName === l.list_name
                    ? "bg-emerald-700 text-white"
                    : "bg-slate-100 text-slate-700"
                }`}
              >
                {l.total_leads}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Filter Toolbar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 bg-white p-3 rounded-xl border border-slate-200 shadow-2xs">
        <div className="flex flex-wrap items-center gap-2 flex-1">
          {/* Search Box */}
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search by name, phone, email..."
              className="modern-input w-full text-xs py-1.5 pl-8 pr-7"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>

          {/* Lead List Filter */}
          <select
            value={selectedListName}
            onChange={(e) => setSelectedListName(e.target.value)}
            className="filter-chip cursor-pointer outline-none text-xs py-1.5 px-2.5 rounded-lg border border-slate-200 bg-slate-50 font-medium"
          >
            <option value="all">All Lead Lists ({leadLists.length})</option>
            {leadLists.map((l: LeadListSummary) => (
              <option key={l.list_name} value={l.list_name}>
                📁 {l.list_name} ({l.total_leads})
              </option>
            ))}
          </select>

          {/* Pipeline Stage Filter */}
          <select
            value={pipelineStageFilter}
            onChange={(e) => setPipelineStageFilter(e.target.value)}
            className="filter-chip cursor-pointer outline-none text-xs py-1.5 px-2.5 rounded-lg border border-slate-200 bg-slate-50 font-medium"
          >
            <option value="all">All Pipeline Stages</option>
            <option value="new">🆕 New Leads</option>
            <option value="contacted">📞 Contacted</option>
            <option value="qualified">🎯 Qualified</option>
            <option value="proposal_sent">📄 Proposal Sent</option>
            <option value="won">🏆 Won Deals</option>
            <option value="lost">❌ Lost / Dead</option>
          </select>

          {/* Tag Filter */}
          <select
            value={selectedTagId}
            onChange={(e) => setSelectedTagId(e.target.value)}
            className="filter-chip cursor-pointer outline-none text-xs py-1.5 px-2.5 rounded-lg border border-slate-200 bg-slate-50"
          >
            <option value="all">All Tags ({tags.length})</option>
            {tags.map((t: Tag) => (
              <option key={t.id} value={t.id}>
                Tag: {t.name}
              </option>
            ))}
          </select>

          {/* Opt-In Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-chip cursor-pointer outline-none text-xs py-1.5 px-2.5 rounded-lg border border-slate-200 bg-slate-50"
          >
            <option value="all">All Statuses</option>
            <option value="opted_in">Opted In Only</option>
            <option value="opted_out">Opted Out Only</option>
          </select>
        </div>

        <div className="flex items-center gap-2 self-end sm:self-auto">
          {selectedListName !== "all" && (
            <button
              onClick={() => navigate("/admin/campaigns")}
              className="btn-primary text-xs py-1.5 px-3 flex items-center gap-1.5 shadow-2xs"
              title="Launch Campaign to this List"
            >
              <Sparkles className="w-3.5 h-3.5" />
              <span>Broadcast to List</span>
            </button>
          )}

          <button
            onClick={() => contactsQuery.refetch()}
            className="btn-secondary text-xs py-1.5 px-3 flex items-center gap-1.5 shadow-2xs"
            title="Refresh contacts"
          >
            <RefreshCw className={`w-3.5 h-3.5 text-slate-500 ${contactsQuery.isFetching ? "animate-spin" : ""}`} />
            <span>Sync</span>
          </button>
        </div>
      </div>

      {/* Contacts Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-2xs">
        <div className="overflow-x-auto">
          <table className="modern-table w-full text-left">
            <thead className="bg-slate-50/80 border-b border-slate-200 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
              <tr>
                <th className="py-3 px-4">Contact</th>
                <th className="py-3 px-4">Phone Number</th>
                <th className="py-3 px-4">Email</th>
                <th className="py-3 px-4">Lead List & Stage</th>
                <th className="py-3 px-4">Tags</th>
                <th className="py-3 px-4">Opt-in Status</th>
                <th className="py-3 px-4">Added Date</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {contactsQuery.isLoading ? (
                <tr>
                  <td colSpan={8} className="text-center py-16 text-slate-400 text-xs">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <RefreshCw className="w-6 h-6 text-emerald-600 animate-spin" />
                      <span className="font-semibold text-slate-600">Loading contacts database...</span>
                    </div>
                  </td>
                </tr>
              ) : contacts.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-16 text-slate-400 text-xs">
                    <div className="max-w-sm mx-auto flex flex-col items-center">
                      <Users className="w-10 h-10 text-slate-300 mb-2" />
                      <p className="font-medium text-slate-700 text-sm">No contacts found</p>
                      <p className="text-slate-400 mt-1 mb-4 text-xs">
                        {searchQuery || statusFilter !== "all" || selectedTagId !== "all"
                          ? "Try clearing your filters or search query."
                          : "Get started by adding your first contact or importing an Excel spreadsheet."}
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setIsImportModalOpen(true)}
                          className="btn-secondary text-xs py-1.5 px-3 flex items-center gap-1.5"
                        >
                          <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" />
                          <span>Import Excel</span>
                        </button>
                        <button
                          onClick={() => setIsAddModalOpen(true)}
                          className="btn-primary text-xs py-1.5 px-3 flex items-center gap-1.5"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          <span>Add Contact</span>
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                contacts.map((contact: Contact) => {
                  const rawName = [contact.first_name, contact.last_name].filter(Boolean).join(" ").trim();
                  const displayName =
                    !rawName ||
                    rawName.startsWith("WhatsApp ") ||
                    rawName.startsWith("Contact ") ||
                    rawName === "WhatsApp User" ||
                    rawName === "WhatsApp Contact"
                      ? "Unknown"
                      : rawName;
                  const initials = displayName
                    .split(" ")
                    .map((n) => n[0])
                    .slice(0, 2)
                    .join("")
                    .toUpperCase();

                  return (
                    <tr key={contact.id} className="hover:bg-slate-50/70 transition-colors">
                      {/* Contact Avatar & Name */}
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center font-bold text-xs shrink-0">
                            {initials}
                          </div>
                          <div>
                            <div className="font-semibold text-slate-900 text-xs">
                              {displayName}
                            </div>
                            <div className="text-[11px] text-slate-400 font-mono sm:hidden">
                              {contact.phone_e164}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Phone Number + Copy */}
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1.5 font-mono text-xs font-semibold text-slate-800">
                          <span>{contact.phone_e164}</span>
                          <button
                            onClick={() => copyText(contact.phone_e164, contact.id)}
                            title="Copy phone"
                            className="p-1 hover:bg-slate-100 rounded text-slate-400 hover:text-slate-700 transition-colors"
                          >
                            {copiedId === contact.id ? (
                              <Check className="w-3 h-3 text-emerald-600" />
                            ) : (
                              <Copy className="w-3 h-3" />
                            )}
                          </button>
                        </div>
                      </td>

                      {/* Email */}
                      <td className="py-3 px-4 text-xs text-slate-600 font-mono">
                        {contact.email || <span className="text-slate-300">—</span>}
                      </td>

                      {/* Lead List & Pipeline Stage */}
                      <td className="py-3 px-4">
                        <div className="flex flex-col gap-1.5 items-start">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium bg-slate-100 text-slate-700 border border-slate-200">
                            <Folder className="w-2.5 h-2.5 text-slate-400" />
                            {contact.attributes?.list_name || "General"}
                          </span>
                          <select
                            value={contact.attributes?.lead_status || "new"}
                            onChange={(e) =>
                              updateStatusMutation.mutate({
                                contactId: contact.id,
                                status: e.target.value,
                              })
                            }
                            disabled={updateStatusMutation.isPending}
                            className={`text-[10px] font-semibold rounded-md px-1.5 py-0.5 border cursor-pointer outline-none transition-colors ${
                              contact.attributes?.lead_status === "won"
                                ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                                : contact.attributes?.lead_status === "qualified" ||
                                  contact.attributes?.lead_status === "proposal_sent"
                                ? "bg-blue-50 text-blue-700 border-blue-300"
                                : contact.attributes?.lead_status === "contacted"
                                ? "bg-amber-50 text-amber-700 border-amber-300"
                                : contact.attributes?.lead_status === "lost"
                                ? "bg-rose-50 text-rose-700 border-rose-300"
                                : "bg-slate-50 text-slate-700 border-slate-200"
                            }`}
                          >
                            <option value="new">New</option>
                            <option value="contacted">Contacted</option>
                            <option value="qualified">Qualified</option>
                            <option value="proposal_sent">Proposal Sent</option>
                            <option value="won">Won 🎉</option>
                            <option value="lost">Lost</option>
                          </select>
                        </div>
                      </td>

                      {/* Tags */}
                      <td className="py-3 px-4">
                        <div className="flex flex-wrap gap-1">
                          {contact.tags && contact.tags.length > 0 ? (
                            contact.tags.map((t) => (
                              <span
                                key={t.id}
                                className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-700 border border-slate-200"
                              >
                                {t.name}
                              </span>
                            ))
                          ) : (
                            <span className="text-slate-300 text-xs">—</span>
                          )}
                        </div>
                      </td>

                      {/* Opt-in Status */}
                      <td className="py-3 px-4">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${
                            contact.opt_in_status === "opted_in"
                              ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                              : "bg-slate-100 text-slate-600 border border-slate-200"
                          }`}
                        >
                          {contact.opt_in_status === "opted_in" ? "Opted In" : "Opted Out"}
                        </span>
                      </td>

                      {/* Added Date */}
                      <td className="py-3 px-4 font-mono text-xs text-slate-500">
                        {contact.created_at ? new Date(contact.created_at).toLocaleDateString() : "—"}
                      </td>

                      {/* Actions */}
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => navigate("/admin/inbox")}
                            title="Chat in Live Inbox"
                            className="p-1.5 rounded-lg hover:bg-emerald-50 text-slate-500 hover:text-emerald-700 transition-colors"
                          >
                            <MessageSquare className="w-3.5 h-3.5 text-emerald-600" />
                          </button>

                          <button
                            onClick={() => {
                              setEditingContact(contact);
                              setEditFirstName(contact.first_name || "");
                              setEditLastName(contact.last_name || "");
                              setEditEmail(contact.email || "");
                              setEditOptIn(contact.opt_in_status || "opted_in");
                            }}
                            title="Edit Contact"
                            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-800 transition-colors"
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>

                          <button
                            onClick={() => setDeletingContact(contact)}
                            title="Delete Contact"
                            className="p-1.5 rounded-lg hover:bg-rose-50 text-slate-500 hover:text-rose-600 transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Contact Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-xs flex items-center justify-center p-4" onClick={() => setIsAddModalOpen(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden border border-slate-100" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <User className="w-4 h-4 text-emerald-600" />
                Add Contact to CRM
              </h3>
              <button
                onClick={() => setIsAddModalOpen(false)}
                className="p-1 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4 space-y-3.5">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  WhatsApp Phone Number (E.164) *
                </label>
                <input
                  type="text"
                  placeholder="+919876543210"
                  value={phoneE164}
                  onChange={(e) => setPhoneE164(e.target.value)}
                  className="modern-input w-full font-mono text-xs"
                  autoFocus
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  Must include country code (e.g., +91 for India, +1 for USA).
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">First Name</label>
                  <input
                    type="text"
                    placeholder="Rajesh"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="modern-input w-full text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Last Name</label>
                  <input
                    type="text"
                    placeholder="Kumar"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="modern-input w-full text-xs"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Email Address</label>
                <input
                  type="email"
                  placeholder="rajesh@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="modern-input w-full text-xs font-mono"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  className="btn-secondary text-xs py-2 px-3.5"
                  onClick={() => setIsAddModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn-primary text-xs py-2 px-4"
                  onClick={() => createContactMutation.mutate()}
                  disabled={!phoneE164.trim() || createContactMutation.isPending}
                >
                  {createContactMutation.isPending ? "Saving..." : "Save Contact"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Contact Modal */}
      {editingContact && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-xs flex items-center justify-center p-4" onClick={() => setEditingContact(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden border border-slate-100" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <Edit3 className="w-4 h-4 text-emerald-600" />
                Edit Contact Details
              </h3>
              <button
                onClick={() => setEditingContact(null)}
                className="p-1 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4 space-y-3.5">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  WhatsApp Phone Number
                </label>
                <input
                  type="text"
                  value={editingContact.phone_e164}
                  className="modern-input w-full font-mono text-xs bg-slate-50 text-slate-500 cursor-not-allowed"
                  disabled
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">First Name</label>
                  <input
                    type="text"
                    placeholder="First Name"
                    value={editFirstName}
                    onChange={(e) => setEditFirstName(e.target.value)}
                    className="modern-input w-full text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Last Name</label>
                  <input
                    type="text"
                    placeholder="Last Name"
                    value={editLastName}
                    onChange={(e) => setEditLastName(e.target.value)}
                    className="modern-input w-full text-xs"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Email Address</label>
                <input
                  type="email"
                  placeholder="contact@company.com"
                  value={editEmail}
                  onChange={(e) => setEditEmail(e.target.value)}
                  className="modern-input w-full text-xs font-mono"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Opt-in Status</label>
                <select
                  value={editOptIn}
                  onChange={(e) => setEditOptIn(e.target.value)}
                  className="modern-input w-full text-xs"
                >
                  <option value="opted_in">Opted In (Active Subscriber)</option>
                  <option value="opted_out">Opted Out / Unsubscribed</option>
                </select>
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  className="btn-secondary text-xs py-2 px-3.5"
                  onClick={() => setEditingContact(null)}
                >
                  Cancel
                </button>
                <button
                  className="btn-primary text-xs py-2 px-4"
                  onClick={() => updateContactMutation.mutate()}
                  disabled={updateContactMutation.isPending}
                >
                  {updateContactMutation.isPending ? "Saving..." : "Update Contact"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Contact Confirmation Modal */}
      {deletingContact && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-xs flex items-center justify-center p-4" onClick={() => setDeletingContact(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden border border-slate-100" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 text-center">
              <div className="w-12 h-12 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center mx-auto mb-3">
                <Trash2 className="w-6 h-6" />
              </div>
              <h3 className="text-base font-bold text-slate-900">Delete Contact?</h3>
              <p className="text-xs text-slate-500 mt-1">
                Are you sure you want to remove{" "}
                <strong className="text-slate-800">
                  {[deletingContact.first_name, deletingContact.last_name].filter(Boolean).join(" ") || deletingContact.phone_e164}
                </strong>{" "}
                ({deletingContact.phone_e164}) from your CRM? This action cannot be undone.
              </p>

              <div className="flex justify-center gap-2 mt-5">
                <button
                  className="btn-secondary text-xs py-2 px-4"
                  onClick={() => setDeletingContact(null)}
                  disabled={deleteContactMutation.isPending}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-xs font-semibold shadow-xs disabled:opacity-50 transition-colors"
                  onClick={() => deleteContactMutation.mutate(deletingContact.id)}
                  disabled={deleteContactMutation.isPending}
                >
                  {deleteContactMutation.isPending ? "Deleting..." : "Yes, Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Excel / CSV Import Modal */}
      {isImportModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto" onClick={() => setIsImportModalOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden border border-slate-100 my-8" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <div>
                <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
                  Import Contacts from Excel or CSV
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Upload an .xlsx, .xls, or .csv file. Columns like Phone, Name, and Email are detected automatically.
                </p>
              </div>
              <button
                onClick={() => setIsImportModalOpen(false)}
                className="p-1 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
              {/* Sample Download Bar */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                <span className="text-xs text-slate-600 font-medium">
                  Need a starting template? Download our ready-to-use sample:
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={downloadSampleExcel}
                    className="text-xs py-1 px-2.5 bg-white border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-100 flex items-center gap-1.5 shadow-2xs font-medium"
                  >
                    <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" />
                    <span>Sample Excel (.xlsx)</span>
                  </button>
                  <button
                    onClick={downloadSampleCsv}
                    className="text-xs py-1 px-2.5 bg-white border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-100 flex items-center gap-1.5 shadow-2xs font-medium"
                  >
                    <FileText className="w-3.5 h-3.5 text-blue-600" />
                    <span>Sample CSV (.csv)</span>
                  </button>
                </div>
              </div>

              {/* Lead Management & Target List Settings */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 space-y-3">
                <div className="flex items-center gap-1.5 text-xs font-bold text-slate-800">
                  <Folder className="w-4 h-4 text-emerald-600" />
                  <span>Lead List & Pipeline Settings</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      Target Lead List Name
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Q4 Real Estate, Gitex 2026"
                      value={importListName}
                      onChange={(e) => setImportListName(e.target.value)}
                      className="modern-input w-full text-xs py-1.5"
                    />
                    <p className="text-[10px] text-slate-400 mt-0.5">Used for targeted campaigns</p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      Initial Pipeline Stage
                    </label>
                    <select
                      value={importLeadStatus}
                      onChange={(e) => setImportLeadStatus(e.target.value)}
                      className="modern-input w-full text-xs py-1.5"
                    >
                      <option value="new">🆕 New Lead</option>
                      <option value="contacted">📞 Contacted</option>
                      <option value="qualified">🎯 Qualified</option>
                      <option value="proposal_sent">📄 Proposal Sent</option>
                    </select>
                    <p className="text-[10px] text-slate-400 mt-0.5">Default stage in pipeline</p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      Attach Tag (Optional)
                    </label>
                    <select
                      value={importTagId}
                      onChange={(e) => setImportTagId(e.target.value)}
                      className="modern-input w-full text-xs py-1.5"
                    >
                      <option value="">No tag</option>
                      {tags.map((t: Tag) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                    <p className="text-[10px] text-slate-400 mt-0.5">Auto-tag imported leads</p>
                  </div>
                </div>
              </div>

              {/* Tabs: File Upload vs Paste Text */}
              <div className="flex border-b border-slate-200">
                <button
                  onClick={() => setImportTab("file")}
                  className={`pb-2 px-3 text-xs font-semibold border-b-2 transition-colors ${
                    importTab === "file"
                      ? "border-emerald-600 text-emerald-700"
                      : "border-transparent text-slate-500 hover:text-slate-700"
                  }`}
                >
                  Upload File (.xlsx, .xls, .csv)
                </button>
                <button
                  onClick={() => setImportTab("paste")}
                  className={`pb-2 px-3 text-xs font-semibold border-b-2 transition-colors ${
                    importTab === "paste"
                      ? "border-emerald-600 text-emerald-700"
                      : "border-transparent text-slate-500 hover:text-slate-700"
                  }`}
                >
                  Paste Text
                </button>
              </div>

              {/* Tab 1: File Dropzone */}
              {importTab === "file" && (
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx, .xls, .csv"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleFileUpload(f);
                    }}
                  />

                  <div
                    onDragOver={(e) => {
                      e.preventDefault();
                      setIsDragging(true);
                    }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setIsDragging(false);
                      const f = e.dataTransfer.files?.[0];
                      if (f) handleFileUpload(f);
                    }}
                    onClick={() => fileInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
                      isDragging
                        ? "border-emerald-500 bg-emerald-50/50"
                        : "border-slate-200 hover:border-emerald-400 bg-slate-50/50"
                    }`}
                  >
                    <Upload className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                    <p className="text-xs font-semibold text-slate-800">
                      {importFile ? importFile.name : "Click to select or drag & drop Excel / CSV file"}
                    </p>
                    <p className="text-[11px] text-slate-400 mt-1">
                      Supports Microsoft Excel (.xlsx, .xls) and Comma-Separated Values (.csv)
                    </p>
                  </div>
                </div>
              )}

              {/* Tab 2: Paste Text */}
              {importTab === "paste" && (
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Paste contacts (one per line, comma or tab separated):
                  </label>
                  <textarea
                    rows={4}
                    placeholder={`+919876543210, Rajesh, Kumar, rajesh@example.com\n+919820011223, Priya, Sharma, priya@example.com`}
                    value={pasteText}
                    onChange={(e) => handlePasteParse(e.target.value)}
                    className="modern-input w-full font-mono text-xs"
                  />
                  <p className="text-[11px] text-slate-400 mt-1">
                    Format: Phone, First Name, Last Name, Email
                  </p>
                </div>
              )}

              {/* Error Message */}
              {importError && (
                <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-rose-700 text-xs flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0 text-rose-600" />
                  <span>{importError}</span>
                </div>
              )}

              {/* Parse Preview Summary & Table */}
              {parsedRows.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                      Preview Data ({parsedRows.length} rows detected)
                    </h4>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold">
                        <CheckCircle2 className="w-3 h-3" />
                        {validParsedCount} Valid
                      </span>
                      {invalidParsedCount > 0 && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200 font-semibold">
                          <AlertCircle className="w-3 h-3" />
                          {invalidParsedCount} Invalid
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="border border-slate-200 rounded-xl overflow-hidden max-h-52 overflow-y-auto">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-slate-50 border-b border-slate-200 text-[11px] font-semibold text-slate-500 uppercase">
                        <tr>
                          <th className="py-2 px-3">Status</th>
                          <th className="py-2 px-3">Phone (E.164)</th>
                          <th className="py-2 px-3">Name</th>
                          <th className="py-2 px-3">Email</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {parsedRows.slice(0, 10).map((row, idx) => (
                          <tr key={idx} className={row.isValid ? "bg-white" : "bg-rose-50/30"}>
                            <td className="py-2 px-3">
                              {row.isValid ? (
                                <span className="inline-flex items-center text-[10px] font-bold text-emerald-600">
                                  <Check className="w-3 h-3 mr-1" /> Valid
                                </span>
                              ) : (
                                <span className="inline-flex items-center text-[10px] font-bold text-rose-600" title={row.error}>
                                  <AlertCircle className="w-3 h-3 mr-1" /> {row.error || "Invalid"}
                                </span>
                              )}
                            </td>
                            <td className="py-2 px-3 font-mono font-medium text-slate-800">
                              {row.phone_e164}
                            </td>
                            <td className="py-2 px-3 text-slate-700">
                              {[row.first_name, row.last_name].filter(Boolean).join(" ") || "—"}
                            </td>
                            <td className="py-2 px-3 font-mono text-slate-500">
                              {row.email || "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {parsedRows.length > 10 && (
                    <p className="text-[11px] text-slate-400 text-center">
                      Showing first 10 of {parsedRows.length} contacts
                    </p>
                  )}
                </div>
              )}

              {/* Progress Indicator */}
              {importProgress && (
                <div className="space-y-1.5 p-3 bg-slate-50 rounded-xl border border-slate-200">
                  <div className="flex justify-between text-xs font-semibold text-slate-700">
                    <span>Importing contacts...</span>
                    <span>
                      {importProgress.current} / {importProgress.total}
                    </span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-emerald-600 h-2 transition-all duration-300 rounded-full"
                      style={{
                        width: `${Math.round((importProgress.current / importProgress.total) * 100)}%`,
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Modal Footer */}
              <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                <button
                  className="btn-secondary text-xs py-2 px-3.5"
                  onClick={() => setIsImportModalOpen(false)}
                  disabled={importMutation.isPending}
                >
                  Cancel
                </button>
                <button
                  className="btn-primary text-xs py-2 px-4 flex items-center gap-1.5 shadow-2xs"
                  onClick={() => importMutation.mutate(parsedRows)}
                  disabled={validParsedCount === 0 || importMutation.isPending}
                >
                  {importMutation.isPending ? (
                    <>
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      <span>Importing...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-3.5 h-3.5" />
                      <span>Import {validParsedCount > 0 ? `${validParsedCount} Contacts` : "Contacts"}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
